import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Search, Loader2, Check, Pencil, X } from "lucide-react";
import { Card } from "./ClienteCharts";
import { toast } from "sonner";

const segnale = L.divIcon({
  className: "",
  html: `<div style="width:22px;height:22px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:#0ea5e9;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.4)"></div>`,
  iconSize: [22, 22], iconAnchor: [11, 22],
});

// ricentra solo al cambio di `trigger` (nuova ricerca), non al trascinamento del pin
const Recenter = ({ pt, trigger }) => {
  const map = useMap();
  useEffect(() => { if (pt) map.setView([pt.lat, pt.lng], Math.max(map.getZoom() || 0, 16)); }, [trigger]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
};

// Card "Località" della Vista Cliente: mostra indirizzo + mappa e permette di rilocalizzare l'impianto
// (ricerca indirizzo + pin trascinabile liberamente, come nei portali Staff/Partner).
export function ClienteLocalita({ imp, src, reload }) {
  const g = imp.impianto_geo || {};
  const puoModificare = !!src.localita; // sessione cliente (no anteprima staff/partner)
  const salvato = g.lat != null && g.lng != null ? { lat: g.lat, lng: g.lng, comune: g.comune, provincia: g.provincia } : null;

  const [editing, setEditing] = useState(false);
  const [addr, setAddr] = useState(g.indirizzo || imp.localita || "");
  const [pt, setPt] = useState(salvato);
  const [trig, setTrig] = useState(0);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);

  const apri = () => {
    setAddr(g.indirizzo || imp.localita || "");
    setPt(salvato);
    setTrig((t) => t + 1);
    setEditing(true);
  };
  const chiudi = () => { setEditing(false); };

  const cerca = async () => {
    if (!addr.trim()) return;
    setSearching(true);
    try {
      const r = await src.localita({ indirizzo: addr, preview: true });
      if (!r.geo) toast.error("Indirizzo non trovato: aggiungi via, numero civico e comune");
      else { setPt(r.geo); setTrig((t) => t + 1); }
    } catch (e) { toast.error(e.response?.data?.detail || "Ricerca non riuscita"); }
    setSearching(false);
  };

  const salva = async () => {
    if (!pt) return;
    setBusy(true);
    try {
      await src.localita({ indirizzo: addr, lat: pt.lat, lng: pt.lng });
      toast.success("Posizione dell'impianto aggiornata");
      setEditing(false);
      reload && reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Salvataggio non riuscito"); }
    setBusy(false);
  };

  const shown = editing ? pt : salvato;
  const etichetta = [g.comune, g.provincia && `(${g.provincia})`].filter(Boolean).join(" ");

  return (
    <Card title="Località" testid="cliente-localita-card">
      {!editing && (
        <div className="space-y-3" data-testid="cliente-localita-vista">
          <div className="flex items-start gap-2">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-sky-500" />
            <p className="min-w-0 text-sm text-slate-700" data-testid="cliente-localita-indirizzo">
              {g.indirizzo || imp.localita || "Posizione non ancora impostata"}
              {etichetta && <span className="block text-xs text-slate-400">{etichetta}</span>}
            </p>
          </div>
          {shown ? (
            <div className="relative isolate h-[200px] overflow-hidden rounded-2xl ring-1 ring-slate-100" data-testid="cliente-localita-map">
              <MapContainer center={[shown.lat, shown.lng]} zoom={16} scrollWheelZoom={false} dragging={false} className="h-full w-full">
                <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <Marker position={[shown.lat, shown.lng]} icon={segnale} />
              </MapContainer>
            </div>
          ) : (
            <p className="rounded-2xl bg-slate-50 px-3 py-3 text-xs text-slate-500">Aggiungi la posizione del tuo impianto per vedere meteo e cielo reali della tua zona.</p>
          )}
          {puoModificare && (
            <button type="button" onClick={apri} data-testid="cliente-localita-modifica"
              className="inline-flex items-center gap-1.5 rounded-2xl bg-slate-900 px-4 py-2 text-sm font-bold text-white transition-opacity hover:opacity-90">
              <Pencil className="h-4 w-4" />{shown ? "Modifica posizione" : "Aggiungi posizione"}
            </button>
          )}
        </div>
      )}

      {editing && (
        <div className="space-y-3" data-testid="cliente-localita-editor">
          <div className="flex gap-2">
            <div className="relative min-w-0 flex-1">
              <MapPin className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-sky-500" />
              <input value={addr} onChange={(e) => setAddr(e.target.value)} onKeyDown={(e) => e.key === "Enter" && cerca()}
                placeholder="Via, civico, comune" data-testid="cliente-localita-input"
                className="h-11 w-full rounded-2xl border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-800 outline-none focus:border-sky-400" />
            </div>
            <button type="button" onClick={cerca} disabled={searching || !addr.trim()} data-testid="cliente-localita-cerca"
              className="inline-flex shrink-0 items-center gap-1 rounded-2xl bg-sky-500 px-3 text-xs font-bold text-white transition-opacity disabled:opacity-40">
              {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}Cerca
            </button>
          </div>

          <div className="relative isolate h-[260px] overflow-hidden rounded-2xl ring-1 ring-slate-100" data-testid="cliente-localita-map-edit">
            <MapContainer center={pt ? [pt.lat, pt.lng] : [44.5, 11]} zoom={pt ? 16 : 6} scrollWheelZoom className="h-full w-full">
              <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <Recenter pt={pt} trigger={trig} />
              {pt && (
                <Marker position={[pt.lat, pt.lng]} draggable icon={segnale}
                  eventHandlers={{ dragend: (e) => { const ll = e.target.getLatLng(); setPt((o) => ({ ...o, lat: +ll.lat.toFixed(6), lng: +ll.lng.toFixed(6) })); } }} />
              )}
            </MapContainer>
            {!pt && <div className="pointer-events-none absolute inset-0 z-[500] flex items-center justify-center bg-black/30 px-6 text-center text-sm text-white" data-testid="cliente-localita-map-vuota">Cerca un indirizzo per vedere il punto sulla mappa</div>}
          </div>

          {pt && <p className="rounded-2xl bg-sky-50 px-3 py-2 text-xs text-sky-700" data-testid="cliente-localita-hint">Trascina il segnalino per posizionarlo sul punto esatto del tuo impianto.</p>}

          <div className="flex items-center gap-2">
            <button type="button" onClick={chiudi} disabled={busy} data-testid="cliente-localita-annulla"
              className="inline-flex items-center gap-1 rounded-2xl border border-slate-200 px-3 py-2 text-sm font-bold text-slate-500 transition-colors hover:text-slate-700">
              <X className="h-4 w-4" />Annulla
            </button>
            <button type="button" onClick={salva} disabled={busy || !pt} data-testid="cliente-localita-salva"
              className="ml-auto inline-flex items-center gap-1.5 rounded-2xl bg-sky-500 px-4 py-2 text-sm font-bold text-white transition-opacity disabled:opacity-40">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}Conferma e salva
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}
